// Users Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';
import { hasPermission } from '../utils/permissions.js';

export async function loadUsers() {
    if (!hasPermission('manage:users')) {
        console.log('[Users] No permission to manage users');
        return;
    }

    try {
        const users = await api.getUsers();
        console.log('[Users] Loaded:', users);
        updateUsersUI(users);
    } catch (error) {
        console.error('[Users] Failed to load:', error);
        showNotification('Failed to load users', 'error');
    }
}

function updateUsersUI(users) {
    const tbody = document.getElementById('users-table-body');
    const adminCount = document.getElementById('admin-count');
    const operatorCount = document.getElementById('operator-count');
    const viewerCount = document.getElementById('viewer-count');

    if (!tbody) {
        console.warn('[Users] users-table-body element not found');
        return;
    }

    const safeUsers = Array.isArray(users) ? users : [];
    const admins = safeUsers.filter(u => u.role === 'admin');
    const operators = safeUsers.filter(u => u.role === 'operator');
    const viewers = safeUsers.filter(u => u.role === 'viewer');
    if (adminCount) adminCount.textContent = admins.length;
    if (operatorCount) operatorCount.textContent = operators.length;
    if (viewerCount) viewerCount.textContent = viewers.length;

    if (safeUsers.length === 0) {
        tbody.innerHTML = `
            <tr>
                <td colspan="4" class="px-6 py-8 text-center text-slate-500 italic">
                    No users found.
                </td>
            </tr>
        `;
        return;
    }

    const attrStr = (v) => JSON.stringify(v == null ? '' : String(v)).replace(/"/g, '&quot;');

    tbody.innerHTML = safeUsers.map(user => {
        const roleColor = user.role === 'admin' ? 'text-neonRed' : (user.role === 'operator' ? 'text-neonCyan' : 'text-neonGreen');
        const last = user.created_at ? new Date(user.created_at).toLocaleDateString() : '—';

        return `
            <tr class="border-b border-slate-800 hover:bg-slate-800/30 transition-all">
                <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white font-bold">
                            ${(user.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div class="font-bold text-white">${escapeHtml(user.name || user.username)}</div>
                            <div class="text-xs text-slate-500">${escapeHtml(user.email || '—')}</div>
                        </div>
                    </div>
                </td>
                <td class="px-6 py-4">
                    <span class="${roleColor} uppercase text-xs font-bold">${escapeHtml(user.role)}</span>
                </td>
                <td class="px-6 py-4 text-slate-400 text-xs">${last}</td>
                <td class="px-6 py-4 text-right">
                    <button type="button" data-testid="users-row-edit" onclick="editUser(${user.id}, ${attrStr(user.username)}, ${attrStr(user.role)}, ${attrStr(user.name || '')}, ${attrStr(user.email || '')})"
                        class="text-neonCyan hover:text-white px-2 py-1 text-xs mr-2" title="Edit">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button type="button" data-testid="users-row-delete" onclick="deleteUser(${user.id})"
                        class="text-neonRed hover:text-white px-2 py-1 text-xs" title="Delete">
                        <i class="fas fa-trash"></i>
                    </button>
                </td>
            </tr>
        `;
    }).join('');
}

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function isUsersViewActive() {
    return !!document.getElementById('view-users');
}

const fallbackDeleteUser = window.deleteUser;
const fallbackOpenAddUserModal = window.openAddUserModal;
const fallbackCloseUserModal = window.closeUserModal;
const fallbackEditUser = window.editUser;
const fallbackHandleUserModalSubmit = window.handleUserModalSubmit;

export async function refreshUsersList() {
    await loadUsers();
}

export async function deleteUser(id) {
    if (!isUsersViewActive() && typeof fallbackDeleteUser === 'function') {
        return fallbackDeleteUser(id);
    }

    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
        await api.deleteUser(id);
        showNotification('User deleted successfully', 'success');
        await loadUsers();
    } catch (error) {
        console.error('[Users] Failed to delete user:', error);
        showNotification('Failed to delete user: ' + (error.message || 'Unknown error'), 'error');
    }
}

export function openAddUserModal() {
    if (typeof fallbackOpenAddUserModal === 'function') {
        return fallbackOpenAddUserModal();
    }
}

export function closeUserModal() {
    if (typeof fallbackCloseUserModal === 'function') {
        return fallbackCloseUserModal();
    }
}

export function editUser(id, username, role, name, email) {
    if (typeof fallbackEditUser === 'function') {
        return fallbackEditUser(id, username, role, name, email);
    }
}

export async function handleUserModalSubmit(event) {
    if (typeof fallbackHandleUserModalSubmit === 'function') {
        return fallbackHandleUserModalSubmit(event);
    }
}

window.loadUsers = loadUsers;
window.refreshUsersList = refreshUsersList;
window.deleteUser = deleteUser;
window.openAddUserModal = openAddUserModal;
window.closeUserModal = closeUserModal;
window.editUser = editUser;
window.handleUserModalSubmit = handleUserModalSubmit;
